/**
 * Planner Service - API client for lesson planning
 *
 * Provides CRUD operations for weekly lesson plans and time blocks.
 * Plans are organized by class and week, with blocks representing
 * scheduled activities within each day.
 */

import { api } from './api-client';
import type {
  Plan,
  PlanWithBlocks,
  Block,
  CreatePlanInput,
  UpdatePlanInput,
  CreateBlockInput,
  UpdateBlockInput,
  PlanResponse,
  PlanWithBlocksResponse,
  BlockResponse,
  PlanListResponse,
} from '@/types/planner.types';

// ============================================================================
// Transformers (snake_case -> camelCase)
// ============================================================================

function transformPlan(data: PlanResponse): Plan {
  return {
    id: data.id,
    classId: data.class_id,
    weekStart: data.week_start,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

function transformBlock(data: BlockResponse): Block {
  return {
    id: data.id,
    planId: data.plan_id,
    dayOfWeek: data.day_of_week,
    startTime: data.start_time,
    endTime: data.end_time,
    subject: data.subject,
    outcomeIds: data.outcome_ids ?? [],
    resourceIds: data.resource_ids ?? [],
    notes: data.notes,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

function transformPlanWithBlocks(data: PlanWithBlocksResponse): PlanWithBlocks {
  return {
    ...transformPlan(data),
    blocks: (data.blocks ?? []).map(transformBlock),
  };
}

// ============================================================================
// Request Transformers (camelCase -> snake_case)
// ============================================================================

function toCreatePlanRequest(input: CreatePlanInput): Record<string, unknown> {
  return {
    class_id: input.classId,
    week_start: input.weekStart,
  };
}

function toUpdatePlanRequest(input: UpdatePlanInput): Record<string, unknown> {
  const request: Record<string, unknown> = {};
  if (input.weekStart !== undefined) {
    request.week_start = input.weekStart;
  }
  return request;
}

function toCreateBlockRequest(input: CreateBlockInput): Record<string, unknown> {
  return {
    day_of_week: input.dayOfWeek,
    start_time: input.startTime,
    end_time: input.endTime,
    subject: input.subject,
    outcome_ids: input.outcomeIds ?? [],
    resource_ids: input.resourceIds ?? [],
    notes: input.notes,
  };
}

function toUpdateBlockRequest(input: UpdateBlockInput): Record<string, unknown> {
  const request: Record<string, unknown> = {};
  if (input.dayOfWeek !== undefined) {
    request.day_of_week = input.dayOfWeek;
  }
  if (input.startTime !== undefined) {
    request.start_time = input.startTime;
  }
  if (input.endTime !== undefined) {
    request.end_time = input.endTime;
  }
  if (input.subject !== undefined) {
    request.subject = input.subject;
  }
  if (input.outcomeIds !== undefined) {
    request.outcome_ids = input.outcomeIds;
  }
  if (input.resourceIds !== undefined) {
    request.resource_ids = input.resourceIds;
  }
  if (input.notes !== undefined) {
    request.notes = input.notes;
  }
  return request;
}

// ============================================================================
// Service Class
// ============================================================================

class PlannerService {
  // --------------------------------------------------------------------------
  // Plans
  // --------------------------------------------------------------------------

  /**
   * Get all plans, optionally filtered by class and/or week.
   *
   * @param classId - Filter by class ID
   * @param weekStart - Filter by week start date (ISO format YYYY-MM-DD)
   * @param signal - AbortSignal for request cancellation
   */
  async getPlans(
    classId?: string,
    weekStart?: string,
    signal?: AbortSignal
  ): Promise<Plan[]> {
    const params = new URLSearchParams();

    if (classId) {
      params.set('class_id', classId);
    }
    if (weekStart) {
      params.set('week_start', weekStart);
    }

    const queryString = params.toString();
    const url = queryString ? `/plans?${queryString}` : '/plans';

    const response = await api.get<{ items: PlanResponse[]; total: number }>(
      url,
      { signal }
    );

    return response.items.map(transformPlan);
  }

  /**
   * Get a single plan with all its blocks.
   *
   * @param planId - The plan ID
   * @param signal - AbortSignal for request cancellation
   */
  async getPlan(planId: string, signal?: AbortSignal): Promise<PlanWithBlocks> {
    const response = await api.get<PlanWithBlocksResponse>(
      `/plans/${planId}`,
      { signal }
    );

    return transformPlanWithBlocks(response);
  }

  /**
   * Create a new lesson plan.
   *
   * @param data - Plan creation data
   * @param signal - AbortSignal for request cancellation
   */
  async createPlan(data: CreatePlanInput, signal?: AbortSignal): Promise<Plan> {
    const response = await api.post<PlanResponse>(
      '/plans',
      toCreatePlanRequest(data),
      { signal }
    );

    return transformPlan(response);
  }

  /**
   * Update an existing plan.
   *
   * @param planId - The plan ID to update
   * @param data - Plan update data
   * @param signal - AbortSignal for request cancellation
   */
  async updatePlan(
    planId: string,
    data: UpdatePlanInput,
    signal?: AbortSignal
  ): Promise<Plan> {
    const response = await api.put<PlanResponse>(
      `/plans/${planId}`,
      toUpdatePlanRequest(data),
      { signal }
    );

    return transformPlan(response);
  }

  /**
   * Delete a plan and all its blocks.
   *
   * @param planId - The plan ID to delete
   * @param signal - AbortSignal for request cancellation
   */
  async deletePlan(planId: string, signal?: AbortSignal): Promise<void> {
    await api.delete(`/plans/${planId}`, { signal });
  }

  // --------------------------------------------------------------------------
  // Blocks
  // --------------------------------------------------------------------------

  /**
   * Create a new block within a plan.
   *
   * @param planId - The parent plan ID
   * @param data - Block creation data
   * @param signal - AbortSignal for request cancellation
   */
  async createBlock(
    planId: string,
    data: CreateBlockInput,
    signal?: AbortSignal
  ): Promise<Block> {
    const response = await api.post<BlockResponse>(
      `/plans/${planId}/blocks`,
      toCreateBlockRequest(data),
      { signal }
    );

    return transformBlock(response);
  }

  /**
   * Update an existing block.
   *
   * @param planId - The parent plan ID
   * @param blockId - The block ID to update
   * @param data - Block update data
   * @param signal - AbortSignal for request cancellation
   */
  async updateBlock(
    planId: string,
    blockId: string,
    data: UpdateBlockInput,
    signal?: AbortSignal
  ): Promise<Block> {
    const response = await api.put<BlockResponse>(
      `/plans/${planId}/blocks/${blockId}`,
      toUpdateBlockRequest(data),
      { signal }
    );

    return transformBlock(response);
  }

  /**
   * Delete a block from a plan.
   *
   * @param planId - The parent plan ID
   * @param blockId - The block ID to delete
   * @param signal - AbortSignal for request cancellation
   */
  async deleteBlock(
    planId: string,
    blockId: string,
    signal?: AbortSignal
  ): Promise<void> {
    await api.delete(`/plans/${planId}/blocks/${blockId}`, { signal });
  }

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------

  /**
   * Get the Monday-Friday bounds for a week containing the given date.
   *
   * @param date - Any date within the desired week
   * @returns Object with start (Monday) and end (Friday) dates
   */
  getWeekBounds(date: Date): { start: Date; end: Date } {
    const d = new Date(date);
    const day = d.getDay();

    // Adjust to Monday (day 0 = Sunday in JS, so we need special handling)
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(d);
    monday.setDate(d.getDate() + diffToMonday);
    monday.setHours(0, 0, 0, 0);

    // Friday is 4 days after Monday
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    friday.setHours(23, 59, 59, 999);

    return { start: monday, end: friday };
  }

  /**
   * Format a date as a week label (e.g., "Jan 20 - Jan 24, 2025").
   *
   * @param date - Any date within the week
   * @returns Formatted week label string
   */
  formatWeekLabel(date: Date): string {
    const { start, end } = this.getWeekBounds(date);

    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    const endDay = end.getDate();
    const year = end.getFullYear();

    // If same month, don't repeat it
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}, ${year}`;
    }

    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  }

  /**
   * Format a date as ISO date string (YYYY-MM-DD) for API requests.
   *
   * @param date - Date to format
   * @returns ISO date string
   */
  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get the week start date (Monday) for a given date as ISO string.
   *
   * @param date - Any date within the week
   * @returns ISO date string for the Monday of that week
   */
  getWeekStartForDate(date: Date): string {
    const { start } = this.getWeekBounds(date);
    return this.formatDateForApi(start);
  }
}

// Export singleton instance
export const plannerService = new PlannerService();
